import { useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Upload, FileText, CheckCircle, AlertCircle, Download, Trash2, ArrowLeft, Home, Shield, Database, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import Navbar from '@/components/navbar';
import { useMutation } from '@tanstack/react-query';
import { MaxSpinner } from "@/components/ui/max-spinner";
import { apiRequest, queryClient } from '@/lib/queryClient';

const TABLE_CONFIGS = {
  artists: {
    name: 'Artists',
    description: 'Upload comprehensive artist data including demographics, social media metrics, and SET scores',
    requiredFields: ['artistId', 'name'],
    optionalFields: [
      'imageUrl', 'genre', 'careerStage', 'profileUrl', 'city', 'state', 'country', 
      'eras', 'bio', 'fanGender', 'fanEthnicity', 'fanAge', 'fanAttributes', 'interests', 
      'brandPreferences', 'brandExclusions', 'setUser', 'sponsorshipProducts', 'facebookUrl', 
      'facebookFollowers', 'instagramUrl', 'instagramFollowers', 'xUrl', 'xFollowers', 
      'spotifyUrl', 'spotifyFollowers', 'spotifyMonthlyListeners', 'youtubeUrl', 'youtubeViews', 
      'tiktokUrl', 'tiktokFollowers', 'setScore'
    ],
    sampleData: 'artistId,name,imageUrl,genre,careerStage,city,state,country,setScore,facebookFollowers,instagramFollowers,spotifyFollowers\nDRAKE001,Drake,https://example.com/drake.jpg,Hip-Hop,Mainstream,Toronto,ON,Canada,9.2,15000000,120000000,8500000'
  },
  venues: {
    name: 'Venues',
    description: 'Upload venue information including names, locations, and capacities',
    requiredFields: ['name', 'city', 'state', 'capacity'],
    optionalFields: ['venueId', 'imageUrl', 'url', 'streetAddress', 'zipCode', 'dmaId', 'country', 'lat', 'lng', 'googlePlaceId'],
    sampleData: 'name,venueId,imageUrl,url,capacity,streetAddress,city,state,zipCode,dmaId,country,lat,lng\nThe Fillmore,FILLMORE_SF,https://example.com/fillmore.jpg,https://thefillmore.com,1150,1805 Geary Blvd,San Francisco,CA,94115,1,USA,37.7846,-122.4339\nRed Rocks Amphitheatre,REDROCKS_CO,https://example.com/redrocks.jpg,https://redrocksonline.com,9525,18300 W Alameda Pkwy,Morrison,CO,80465,2,USA,39.6654,-105.2057'
  },
  dmas: {
    name: 'DMAs',
    description: 'Upload DMA data including demographics, population, and geographic coverage',
    requiredFields: ['name', 'dmaCode'],
    optionalFields: ['majorCities', 'rank', 'zipCodes', 'states', 'population', 'demographics', 'imageUrl', 'region'],
    sampleData: 'name,dmaCode,majorCities,rank,zipCodes,states,population,demographics,imageUrl\nNew York,501,"New York City, Newark",1,"10001,10002,07101","NY,NJ",20140470,"{""age"":{""18-34"":28,""35-54"":26},""gender"":{""male"":48,""female"":52}}",https://example.com/nyc.jpg\nLos Angeles,803,"Los Angeles, Long Beach",2,"90210,90211,90028","CA",13200998,"{""age"":{""18-34"":30,""35-54"":25},""gender"":{""male"":49,""female"":51}}",https://example.com/la.jpg'
  },
  shows: {
    name: 'Shows',
    description: 'Upload show data with artist and venue references',
    requiredFields: ['date'],
    optionalFields: ['showId', 'artistName', 'venueName', 'artistId', 'venueId', 'time', 'description', 'sponsorable', 'priceTiers'],
    sampleData: 'showId,artistName,venueName,artistId,venueId,date,time,description,sponsorable,priceTiers\nSHOW001,Drake,Madison Square Garden,DRAKE001,MSG001,2025-08-15,20:00,Summer tour,true,"{""premium"": 25000, ""standard"": 15000, ""basic"": 5000}"\nSHOW002,Ariana Grande,Barclays Center,ARIANA001,BARCLAYS001,2025-09-10,19:30,World tour,true,"{""premium"": 30000, ""standard"": 18000, ""basic"": 8000}"'
  }
};

export default function DataImport() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();

  // All useState hooks must be at the top before any conditional logic
  const [selectedTable, setSelectedTable] = useState('');
  const [importData, setImportData] = useState('');
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [updateOptions, setUpdateOptions] = useState<Record<string, 'overwrite' | 'blank_only'>>({});
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'table-selection' | 'mapping' | 'preview'>('upload');
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  
  // Social Media Refresh states
  const [selectedArtistIds, setSelectedArtistIds] = useState<string>('');
  const [selectedCareerStage, setSelectedCareerStage] = useState<string>('');
  const [refreshMode, setRefreshMode] = useState<'manual' | 'all' | 'career_stage'>('manual');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // All useMutation hooks must also be at the top
  const importMutation = useMutation({
    mutationFn: async (data: { table: string; data: any[]; mapping: Record<string, string>; updateOptions: Record<string, 'overwrite' | 'blank_only'> }) => {
      const response = await apiRequest(`/api/import/${data.table}`, { 
        method: 'POST',
        body: {
          data: data.data, 
          mapping: data.mapping, 
          updateOptions: data.updateOptions 
        }
      });
      return response;
    },
    onSuccess: (result) => {
      const message = result.imported > 0 && result.updated > 0 
        ? `Successfully imported ${result.imported} new records and updated ${result.updated} existing records`
        : result.imported > 0 
        ? `Successfully imported ${result.imported} new records`
        : `Successfully updated ${result.updated} existing records`;
      
      toast({
        title: 'Import Successful',
        description: message,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/${selectedTable}`] });
      setImportData('');
      setPreviewData([]);
      setFieldMapping({});
      setUpdateOptions({});
      setCurrentStep('upload');
    },
    onError: (error: any) => {
      // Import error occurred
      let errorMessage = 'Failed to import data';
      
      if (error.message) {
        // Extract the actual error message from the HTTP error format
        const match = error.message.match(/^\d+: (.+)$/);
        errorMessage = match ? match[1] : error.message;
      }
      
      toast({
        title: 'Import Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Social Media Refresh Mutation
  const refreshMutation = useMutation({
    mutationFn: async (params: { mode: string; artistIds?: number[]; careerStage?: string; batchMode?: boolean }) => {
      const response = await apiRequest('/api/social-media/refresh', {
        method: 'POST',
        body: JSON.stringify(params)
      });
      // apiRequest already returns parsed JSON or null, no need to call .json() again
      return response;
    },
    onSuccess: (result) => {
      const successMessage = result.batchProcessed 
        ? `✅ Batch refresh completed: ${result.success} artists refreshed successfully${result.failed > 0 ? `, ${result.failed} failed` : ''} across ${result.totalBatches} batches`
        : `✅ Refreshed ${result.success} artists successfully${result.failed > 0 ? `, ${result.failed} failed` : ''} (Total processed: ${result.totalArtists || result.success + result.failed})`;
      
      toast({
        title: result.batchProcessed ? 'Batch Social Media Refresh Complete' : 'Social Media Refresh Complete',
        description: successMessage,
      });
      setSelectedArtistIds('');
      setSelectedCareerStage('');
      setIsRefreshing(false);
    },
    onError: (error: any) => {
      // Refresh error occurred
      let errorMessage = 'Failed to refresh social media data';
      
      if (error.message) {
        const match = error.message.match(/^\d+: (.+)$/);
        errorMessage = match ? match[1] : error.message;
      }
      
      // Enhanced error handling for authentication issues
      if (error.message?.includes('401')) {
        errorMessage = 'Authentication required - please log in as an admin';
      } else if (error.message?.includes('403')) {
        errorMessage = 'Admin privileges required to refresh social media data';
      } else if (error.message?.includes('Use batch mode for datasets larger than 100 artists')) {
        errorMessage = 'Too many artists selected. Enable batch processing for large datasets.';
      } else if (error.message?.includes('Cannot refresh more than 100 artists')) {
        errorMessage = 'Too many artists selected (>100). Enable batch processing or reduce selection.';
      }
      
      toast({
        title: 'Refresh Failed',
        description: errorMessage,
        variant: 'destructive',
      });
      setIsRefreshing(false);
    },
  });

  // Spotify Sync Mutation
  const spotifySyncMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('/api/social-media/sync-spotify-data', {
        method: 'POST'
      });
      return response;
    },
    onSuccess: (result) => {
      toast({
        title: 'Spotify Data Sync Complete',
        description: `Successfully updated ${result.successCount} artists with fresh streaming data and high-res images. ${result.imageUpdateCount} images updated.`,
      });
    },
    onError: (error: any) => {
      let errorMessage = 'Failed to sync Spotify data';
      if (error.message) {
        const match = error.message.match(/^\d+: (.+)$/);
        errorMessage = match ? match[1] : error.message;
      }
      toast({
        title: 'Spotify Sync Failed', 
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // All useCallback hooks must also be at the top
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // processFile will be defined after conditional returns
      if (files[0] && files[0].name.endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          setImportData(text);
          setUploadedFileName(files[0].name);
          // parseCSVHeaders will be called in useEffect or component logic
        };
        reader.readAsText(files[0]);
      } else {
        toast({
          title: 'Invalid File Type',
          description: 'Please upload a CSV file',
          variant: 'destructive',
        });
      }
    }
  }, [toast, setImportData, setUploadedFileName]);

  // Check if user is admin - simplified condition
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  // Show loading state while user data is being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-24">
          <div className="text-center">
            <MaxSpinner size="lg" className="mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">Loading...</h1>
            <p className="text-neutral-600">Checking your permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect if not admin
  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-24">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-neutral-900 mb-4">Access Restricted</h1>
            <p className="text-lg text-neutral-600 mb-8">
              This page is restricted to administrators only. Please contact your system administrator for access.
            </p>
            <Button onClick={() => window.location.href = '/'}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle Social Media Refresh
  const handleSocialMediaRefresh = () => {
    if (refreshMode === 'manual') {
      if (!selectedArtistIds.trim()) {
        toast({
          title: 'No Artist IDs',
          description: 'Please enter artist IDs to refresh',
          variant: 'destructive',
        });
        return;
      }

      // Parse comma-separated artist IDs
      const ids = selectedArtistIds
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));

      if (ids.length === 0) {
        toast({
          title: 'Invalid Artist IDs',
          description: 'Please enter valid numeric artist IDs separated by commas',
          variant: 'destructive',
        });
        return;
      }

      setIsRefreshing(true);
      // Use batch mode for manual selection if more than 100 artists
      const useBatchMode = ids.length > 100;
      refreshMutation.mutate({ mode: 'manual', artistIds: ids, batchMode: useBatchMode });
    } else if (refreshMode === 'career_stage') {
      if (!selectedCareerStage) {
        toast({
          title: 'No Career Stage Selected',
          description: 'Please select a career stage to refresh',
          variant: 'destructive',
        });
        return;
      }

      setIsRefreshing(true);
      // Always use batch mode for career stage since it could be large
      refreshMutation.mutate({ mode: 'career_stage', careerStage: selectedCareerStage, batchMode: true });
    } else if (refreshMode === 'all') {
      // Confirm before refreshing all artists
      if (!confirm('This will refresh social media data for ALL artists in the database. This may take a long time and use many API calls. Continue?')) {
        return;
      }

      setIsRefreshing(true);
      // Always use batch mode for all artists
      refreshMutation.mutate({ mode: 'all', batchMode: true });
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a CSV file',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setImportData(text);
      setUploadedFileName(file.name);
      parseCSVHeaders(text);
    };
    reader.readAsText(file);
  };



  const parseCSVHeaders = (csvText: string) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      toast({
        title: 'Invalid CSV',
        description: 'CSV must have at least header row and one data row',
        variant: 'destructive',
      });
      return;
    }

    // Better CSV parsing that handles quoted fields
    const parseCsvLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote mode
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          // End of field
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      // Add the last field
      result.push(current.trim());
      return result;
    };

    const headers = parseCsvLine(lines[0]);
    setCsvHeaders(headers);

    const data = lines.slice(1).map(line => {
      const values = parseCsvLine(line);
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });

    setPreviewData(data.slice(0, 5)); // Show first 5 rows for preview
    setCurrentStep('table-selection');
  };

  const handleTextImport = () => {
    if (!importData.trim()) return;
    parseCSVHeaders(importData);
  };

  const handleTableSelection = (tableType: string) => {
    setSelectedTable(tableType);
    
    // Auto-map fields based on similar names
    const config = TABLE_CONFIGS[tableType as keyof typeof TABLE_CONFIGS];
    const autoMapping: Record<string, string> = {};
    const autoUpdateOptions: Record<string, 'overwrite' | 'blank_only'> = {};
    
    [...config.requiredFields, ...config.optionalFields].forEach(field => {
      const matchingHeader = csvHeaders.find(h => 
        h.toLowerCase().includes(field.toLowerCase()) || 
        field.toLowerCase().includes(h.toLowerCase())
      );
      if (matchingHeader) {
        autoMapping[field] = matchingHeader;
        autoUpdateOptions[field] = 'overwrite';
      }
    });
    
    setFieldMapping(autoMapping);
    setUpdateOptions(autoUpdateOptions);
    setCurrentStep('mapping');
  };

  const handleImport = () => {
    if (previewData.length === 0) {
      toast({
        title: 'No Data',
        description: 'Please upload or paste data first',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedTable) return;
    const config = TABLE_CONFIGS[selectedTable as keyof typeof TABLE_CONFIGS];
    const missingRequired = config.requiredFields.filter(field => !fieldMapping[field]);
    
    if (missingRequired.length > 0) {
      toast({
        title: 'Missing Required Fields',
        description: `Please map these required fields: ${missingRequired.join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    // Parse all data with current mapping
    const lines = importData.trim().split('\n');
    
    // Better CSV parsing that handles quoted fields
    const parseCsvLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote mode
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          // End of field
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      // Add the last field
      result.push(current.trim());
      return result;
    };

    const headers = parseCsvLine(lines[0]);
    const allData = lines.slice(1).map(line => {
      const values = parseCsvLine(line);
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });

    importMutation.mutate({
      table: selectedTable,
      data: allData,
      mapping: fieldMapping,
      updateOptions: updateOptions
    });
  };

  const handleNextStep = () => {
    if (currentStep === 'mapping') {
      if (!selectedTable) return;
      const config = TABLE_CONFIGS[selectedTable as keyof typeof TABLE_CONFIGS];
      const missingRequired = config.requiredFields.filter(field => !fieldMapping[field]);
      
      if (missingRequired.length > 0) {
        toast({
          title: 'Missing Required Fields',
          description: `Please map these required fields: ${missingRequired.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }
      setCurrentStep('preview');
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 'preview') {
      setCurrentStep('mapping');
    } else if (currentStep === 'mapping') {
      setCurrentStep('table-selection');
      setSelectedTable('');
      setFieldMapping({});
      setUpdateOptions({});
    } else if (currentStep === 'table-selection') {
      setCurrentStep('upload');
      setImportData('');
      setPreviewData([]);
      setFieldMapping({});
      setUpdateOptions({});
      setCsvHeaders([]);
      setUploadedFileName('');
    }
  };

  const downloadSample = () => {
    if (!selectedTable) return;
    const config = TABLE_CONFIGS[selectedTable as keyof typeof TABLE_CONFIGS];
    const element = document.createElement('a');
    const file = new Blob([config.sampleData], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = `${selectedTable}_sample.csv`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const config = selectedTable ? TABLE_CONFIGS[selectedTable as keyof typeof TABLE_CONFIGS] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pt-24">
        {/* Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Link href="/">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Link href="/">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Home
                </Button>
              </Link>
              <Link href="/admin/profile">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Admin Profile
                </Button>
              </Link>
              <Link href="/admin/filters">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Filter Management
                </Button>
              </Link>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Database className="h-8 w-8" />
            Data Import
          </h1>
          <p className="text-gray-600">Upload and map your data to populate the database with artists, venues, shows, and markets</p>
        </div>

        {/* Step Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${currentStep === 'upload' ? 'text-blue-600' : currentStep === 'table-selection' || currentStep === 'mapping' || currentStep === 'preview' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'upload' ? 'bg-blue-100' : currentStep === 'table-selection' || currentStep === 'mapping' || currentStep === 'preview' ? 'bg-green-100' : 'bg-gray-100'}`}>
                {currentStep === 'table-selection' || currentStep === 'mapping' || currentStep === 'preview' ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className="ml-2 font-medium">Upload Data</span>
            </div>
            <div className={`flex items-center ${currentStep === 'table-selection' ? 'text-blue-600' : currentStep === 'mapping' || currentStep === 'preview' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'table-selection' ? 'bg-blue-100' : currentStep === 'mapping' || currentStep === 'preview' ? 'bg-green-100' : 'bg-gray-100'}`}>
                {currentStep === 'mapping' || currentStep === 'preview' ? <CheckCircle className="w-5 h-5" /> : '2'}
              </div>
              <span className="ml-2 font-medium">Select Data Type</span>
            </div>
            <div className={`flex items-center ${currentStep === 'mapping' ? 'text-blue-600' : currentStep === 'preview' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'mapping' ? 'bg-blue-100' : currentStep === 'preview' ? 'bg-green-100' : 'bg-gray-100'}`}>
                {currentStep === 'preview' ? <CheckCircle className="w-5 h-5" /> : '3'}
              </div>
              <span className="ml-2 font-medium">Map Fields</span>
            </div>
            <div className={`flex items-center ${currentStep === 'preview' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'preview' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                4
              </div>
              <span className="ml-2 font-medium">Preview & Import</span>
            </div>
          </div>
        </div>

        {/* Step 1: Upload Data */}
        {currentStep === 'upload' && (
          <Card>
            <CardHeader>
              <CardTitle>Upload CSV Data</CardTitle>
              <CardDescription>
                Upload a CSV file with your data. After uploading, you'll choose the data type.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Drag and Drop Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Drag and drop your CSV file here
                  </p>
                  <p className="text-gray-600 mb-4">or</p>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <Label htmlFor="file-upload">
                    <Button variant="outline" asChild>
                      <span>Choose File</span>
                    </Button>
                  </Label>
                </div>

                {/* Data Types Available */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">Available Data Types</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(TABLE_CONFIGS).map(([key, tableConfig]) => (
                      <div key={key} className="bg-white p-3 rounded border">
                        <div className="font-medium">{tableConfig.name}</div>
                        <div className="text-sm text-gray-600">{tableConfig.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Alternative Text Input */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Or paste CSV data directly</h3>
                  </div>
                  <Textarea
                    placeholder="Paste your CSV data here..."
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <Button onClick={handleTextImport} disabled={!importData.trim()}>
                    Parse CSV Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Data Type */}
        {currentStep === 'table-selection' && (
          <Card>
            <CardHeader>
              <CardTitle>Select Data Type</CardTitle>
              <CardDescription>
                Choose what type of data you're importing from your CSV file: {uploadedFileName}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(TABLE_CONFIGS).map(([key, tableConfig]) => (
                    <div key={key} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => handleTableSelection(key)}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-lg">{tableConfig.name}</h3>
                        <Button variant="outline" size="sm">
                          Select
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{tableConfig.description}</p>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium">Required: </span>
                          <span className="text-sm text-red-600">{tableConfig.requiredFields.join(', ')}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium">Optional: </span>
                          <span className="text-sm text-gray-500">{tableConfig.optionalFields.slice(0, 3).join(', ')}{tableConfig.optionalFields.length > 3 ? '...' : ''}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Data Preview */}
                {previewData.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-medium mb-3">Data Preview</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-96 overflow-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              {csvHeaders.map((header, headerIndex) => (
                                <th key={`${header}-${headerIndex}`} className="text-left p-3 font-medium">{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((row, index) => (
                              <tr key={index} className="border-t">
                                {csvHeaders.map((header, headerIndex) => (
                                  <td key={`${header}-${headerIndex}`} className="p-3">{row[header]}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Navigation */}
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={handlePreviousStep}>
                    Previous
                  </Button>
                  <div className="text-sm text-gray-500">
                    Select a data type to continue
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Map Fields */}
        {currentStep === 'mapping' && config && (
          <Card>
            <CardHeader>
              <CardTitle>Map Fields</CardTitle>
              <CardDescription>
                Map your CSV columns to database fields and choose update options.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...config.requiredFields, ...config.optionalFields].map(field => (
                    <div key={field} className="space-y-2">
                      <Label className="font-medium">
                        {field}
                        {config.requiredFields.includes(field) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Select
                          value={fieldMapping[field] || '__none__'}
                          onValueChange={(value) => setFieldMapping(prev => ({ ...prev, [field]: value === '__none__' ? '' : value }))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select CSV column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No mapping</SelectItem>
                            {csvHeaders.map((header, headerIndex) => (
                              <SelectItem key={`${header}-${headerIndex}`} value={header}>{header}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {fieldMapping[field] && (
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={updateOptions[field] === 'overwrite'}
                              onCheckedChange={(checked) => 
                                setUpdateOptions(prev => ({ 
                                  ...prev, 
                                  [field]: checked ? 'overwrite' : 'blank_only' 
                                }))
                              }
                            />
                            <Label className="text-sm">
                              {updateOptions[field] === 'overwrite' ? 'Overwrite' : 'Blank only'}
                            </Label>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-2">Update Options</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Overwrite:</strong> Replace existing data with new values</li>
                    <li>• <strong>Blank only:</strong> Only update if the field is currently empty</li>
                  </ul>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={handlePreviousStep}>
                    Previous
                  </Button>
                  <Button onClick={handleNextStep}>
                    Next: Preview Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Preview & Import */}
        {currentStep === 'preview' && config && (
          <Card>
            <CardHeader>
              <CardTitle>Preview & Import</CardTitle>
              <CardDescription>
                Review your data before importing to {config.name} table.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Preview Data */}
                {previewData.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2">Data Preview (First 5 rows)</h3>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-50">
                            {Object.keys(fieldMapping).filter(field => fieldMapping[field]).map(field => (
                              <th key={field} className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">
                                {field} → {fieldMapping[field]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.map((row, index) => (
                            <tr key={index}>
                              {Object.keys(fieldMapping).filter(field => fieldMapping[field]).map(field => (
                                <td key={field} className="border border-gray-300 px-4 py-2 text-sm">
                                  {row[fieldMapping[field]] || ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={handlePreviousStep}>
                    Previous
                  </Button>
                  <Button 
                    onClick={handleImport} 
                    disabled={importMutation.isPending}
                  >
                    {importMutation.isPending ? (
                      <>
                        <AlertCircle className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Import Data
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Social Media API Refresh Section */}
        <div className="mt-12 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5" />
                Social Media API Refresh
              </CardTitle>
              <CardDescription>
                Manually refresh real-time social media data for specific artists. This will fetch live follower counts and engagement metrics from Instagram, TikTok, YouTube, and Spotify.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* API Status Card */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                      <Wifi className="h-4 w-4" />
                      API Status
                    </h3>
                    <div className="text-sm text-blue-800 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Instagram API: Active</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>TikTok API: Active</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>YouTube API: Active</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Spotify API: Active</span>
                      </div>
                    </div>
                  </div>

                  {/* Cost Information */}
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h3 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      API Cost Information
                    </h3>
                    <div className="text-sm text-yellow-800 space-y-1">
                      <div>• Each artist = 4 API calls</div>
                      <div>• Manual refresh only (no automatic calls)</div>
                      <div>• Updates database with follower counts AND engagement/growth rates</div>
                      <div>• Data cached temporarily (1 hour)</div>
                      <div>• Manual: No limit (batch processing)</div>
                      <div>• Career Stage/All: Automatic batch processing</div>
                    </div>
                  </div>
                </div>

                {/* Refresh Mode Selection */}
                <div className="space-y-4">
                  <Label className="font-medium">Refresh Mode</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div 
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        refreshMode === 'manual' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setRefreshMode('manual')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input 
                          type="radio" 
                          checked={refreshMode === 'manual'}
                          onChange={() => setRefreshMode('manual')}
                          className="text-blue-600"
                        />
                        <h3 className="font-medium">Manual Selection</h3>
                      </div>
                      <p className="text-sm text-gray-600">Enter specific artist IDs</p>
                    </div>

                    <div 
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        refreshMode === 'career_stage' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setRefreshMode('career_stage')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input 
                          type="radio" 
                          checked={refreshMode === 'career_stage'}
                          onChange={() => setRefreshMode('career_stage')}
                          className="text-blue-600"
                        />
                        <h3 className="font-medium">By Career Stage</h3>
                      </div>
                      <p className="text-sm text-gray-600">Refresh all artists in a career stage</p>
                    </div>

                    <div 
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        refreshMode === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setRefreshMode('all')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input 
                          type="radio" 
                          checked={refreshMode === 'all'}
                          onChange={() => setRefreshMode('all')}
                          className="text-blue-600"
                        />
                        <h3 className="font-medium">All Artists</h3>
                      </div>
                      <p className="text-sm text-gray-600">Refresh every artist (⚠️ High API usage)</p>
                    </div>
                  </div>
                </div>

                {/* Manual Artist IDs Input */}
                {refreshMode === 'manual' && (
                  <div className="space-y-2">
                    <Label htmlFor="artist-ids" className="font-medium">
                      Artist IDs to Refresh
                    </Label>
                    <Textarea
                      id="artist-ids"
                      placeholder="Enter artist IDs separated by commas (e.g., 1, 2, 3, 25, 30)"
                      value={selectedArtistIds}
                      onChange={(e) => setSelectedArtistIds(e.target.value)}
                      rows={3}
                      className="font-mono text-sm"
                      disabled={isRefreshing || refreshMutation.isPending}
                    />
                    <p className="text-sm text-gray-600">
                      Enter numeric artist IDs separated by commas. You can find artist IDs in the database or from the browse pages. <strong>No limit on number of artists</strong> - batch processing will handle large datasets automatically.
                    </p>
                  </div>
                )}

                {/* Career Stage Selection */}
                {refreshMode === 'career_stage' && (
                  <div className="space-y-2">
                    <Label htmlFor="career-stage" className="font-medium">
                      Select Career Stage
                    </Label>
                    <Select value={selectedCareerStage} onValueChange={setSelectedCareerStage}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a career stage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mainstream 4">Mainstream 4</SelectItem>
                        <SelectItem value="Mainstream 3">Mainstream 3</SelectItem>
                        <SelectItem value="Mainstream 2">Mainstream 2</SelectItem>
                        <SelectItem value="Mainstream 1">Mainstream 1</SelectItem>
                        <SelectItem value="Emerging">Emerging</SelectItem>
                        <SelectItem value="Underground">Underground</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-gray-600">
                      All artists in the selected career stage will be refreshed.
                    </p>
                  </div>
                )}

                {/* All Artists Warning */}
                {refreshMode === 'all' && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <h3 className="font-medium text-orange-900">Batch Processing Enabled</h3>
                    </div>
                    <p className="text-sm text-orange-800">
                      <strong>All artists will be processed in batches of 100.</strong> The database contains 1600+ artists, so this will be processed automatically in ~17 batches with 1-second delays between batches to prevent API rate limiting. This may take several minutes to complete.
                    </p>
                  </div>
                )}

                {/* Refresh Button */}
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {refreshMode === 'manual' && selectedArtistIds.trim() && (
                      <span>
                        Ready to refresh {selectedArtistIds.split(',').filter(id => id.trim() && !isNaN(parseInt(id.trim()))).length} artists
                      </span>
                    )}
                    {refreshMode === 'career_stage' && selectedCareerStage && (
                      <span>
                        Ready to refresh all {selectedCareerStage} artists
                      </span>
                    )}
                    {refreshMode === 'all' && (
                      <span>
                        Ready to refresh ALL artists in database
                      </span>
                    )}
                  </div>
                  <Button 
                    onClick={handleSocialMediaRefresh}
                    disabled={
                      isRefreshing || 
                      refreshMutation.isPending || 
                      (refreshMode === 'manual' && !selectedArtistIds.trim()) ||
                      (refreshMode === 'career_stage' && !selectedCareerStage)
                    }
                    className="flex items-center gap-2"
                    variant={refreshMode === 'all' ? 'destructive' : 'default'}
                  >
                    {(isRefreshing || refreshMutation.isPending) ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        {refreshMode === 'all' ? 'Refresh ALL Artists' : 'Refresh Social Media Data'}
                      </>
                    )}
                  </Button>
                </div>

                {/* Spotify Sync Section */}
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                        <Wifi className="h-4 w-4" />
                        Spotify API Sync
                      </h3>
                      <p className="text-sm text-green-800 mb-2">
                        Sync fresh streaming data and high-resolution profile images for all artists with Spotify IDs.
                      </p>
                      <p className="text-xs text-green-700">
                        Updates follower counts, popularity scores, and authentic Spotify profile images
                      </p>
                    </div>
                    <Button 
                      onClick={() => spotifySyncMutation.mutate()}
                      disabled={spotifySyncMutation.isPending}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                    >
                      {spotifySyncMutation.isPending ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          Sync Spotify Data
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">How to Use</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Manual Selection</h4>
                      <ol className="space-y-1">
                        <li>1. Choose "Manual Selection"</li>
                        <li>2. Enter specific artist IDs</li>
                        <li>3. Click refresh button</li>
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">By Career Stage</h4>
                      <ol className="space-y-1">
                        <li>1. Choose "By Career Stage"</li>
                        <li>2. Select career stage</li>
                        <li>3. Click refresh button</li>
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">All Artists</h4>
                      <ol className="space-y-1">
                        <li>1. Choose "All Artists"</li>
                        <li>2. Confirm high API usage</li>
                        <li>3. Wait for completion</li>
                      </ol>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">
                    <strong>Note:</strong> Processing may take 1-2 minutes per artist. Fresh follower counts, engagement rates, and growth metrics are saved directly to the database and will be available immediately in artist modals.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}